import React, { useState } from "react";
import AppConfig from "../../config/AppConfig";
import { getOrderTypeIconForLocationMap } from "./OrderTypeIcons";
import MapPlaceholder from "../../../assets/images/map.png";

// Order-details location thumbnail — lazy / click-to-load.
//
// Default: a local blurred placeholder + order-type pin + "Show map" button. This
// makes NO Google request, so viewing an order costs nothing. Only when the user
// clicks do we load the Google Static Map ($2 / 1K), with a top-right button to
// open the location in Google Maps.
const THUMB_HEIGHT = 130; // px
const THUMB_ZOOM = 14; // address-level thumbnail
const STATIC_SIZE = "640x130"; // logical size (<=640 wide); scale=2 for retina

// Coordinates can arrive under a few shapes depending on which serializer built
// the payload: `address`/`l_address` carry GeoJSON `coordinates: [lng, lat]`, and
// some responses only carry `lat_long`. Resolve them all → { lat, lng }.
//
// Strict parser: Number('') is 0, so a blank coordinate would otherwise pin
// (0,0) "null island" — reject null/'' and anything non-finite instead.
const toCoord = (value) => {
  if (value == null || value === "") return NaN;
  const num = Number(value);
  return Number.isFinite(num) ? num : NaN;
};

const resolveLatLng = (location) => {
  const addr = location?.address || location?.l_address;
  const coords = addr?.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    const lng = toCoord(coords[0]); // GeoJSON order is [lng, lat]
    const lat = toCoord(coords[1]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
  }

  if (addr?.latitude != null && addr?.longitude != null) {
    const lat = toCoord(addr.latitude);
    const lng = toCoord(addr.longitude);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
  }

  const ll = location?.lat_long; // "lat,lng" string or [lng, lat] array
  if (typeof ll === "string" && ll.includes(",")) {
    const [lat, lng] = ll.split(",").map((v) => toCoord(v.trim()));
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
  }
  if (Array.isArray(ll) && ll.length >= 2) {
    // codebase convention for lat_long ARRAYS is [lng, lat]
    // (e.g. RoutesMap: lat = stop.lat_long[1]) — keep the same order here
    const lng = toCoord(ll[0]);
    const lat = toCoord(ll[1]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
  }

  return null;
};

// Order-type pin, anchored bottom-centre at the thumbnail centre.
const PinOverlay = ({ icon }) => (
  <div
    style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -100%)",
      pointerEvents: "none",
    }}
  >
    {icon}
  </div>
);

const AddressLocationMap = ({ location, orderTypeConfig = {} }) => {
  const [showMap, setShowMap] = useState(false);
  // static image failed to load (key without Static Maps API, quota, offline)
  const [mapError, setMapError] = useState(false);
  const point = resolveLatLng(location);

  // No usable coordinates → stable placeholder, never a silent blank.
  if (!point) {
    return (
      <div
        style={{
          width: "100%",
          height: `${THUMB_HEIGHT}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f2f3f5",
          color: "#9aa1a9",
          fontSize: "12px",
        }}
      >
        Location not available
      </div>
    );
  }

  const { lat, lng } = point;

  const generateOrderCombinedCode = () => {
    const resolvedCode = orderTypeConfig?.extraCode || orderTypeConfig?.code;
    return `${resolvedCode}-${location?.label_code || ""}`;
  };

  const orderTypeIcon = getOrderTypeIconForLocationMap(
    generateOrderCombinedCode(),
    { height: "40", width: "40" }
  );

  // ── Static map (loaded only after a click) ──────────────────────────────────
  if (showMap) {
    const staticMapUrl =
      `https://maps.googleapis.com/maps/api/staticmap` +
      `?center=${lat},${lng}` +
      `&zoom=${THUMB_ZOOM}` +
      `&size=${STATIC_SIZE}` +
      `&scale=2` +
      `&key=${AppConfig.mapKey}`;
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: `${THUMB_HEIGHT}px`,
          overflow: "hidden",
        }}
      >
        {mapError ? (
          // Static Maps request failed (key not enabled for the Static Maps
          // API, quota, network) — show a stable state instead of a broken
          // image; the external Google Maps link below still works.
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f2f3f5",
              color: "#9aa1a9",
              fontSize: "12px",
            }}
          >
            Map preview unavailable — use the button to open Google Maps
          </div>
        ) : (
          <img
            src={staticMapUrl}
            alt="Location map"
            onError={() => setMapError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
        {!mapError && <PinOverlay icon={orderTypeIcon} />}
        {/* top-right: open this location in Google Maps (new tab) */}
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in Google Maps"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: 6,
            background: "rgba(255,255,255,0.95)",
            color: "#33404d",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            textDecoration: "none",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      </div>
    );
  }

  // ── Default: zero-cost blurred preview + "Show map" button ──────────────────
  const openMap = () => setShowMap(true);

  return (
    <div
      onClick={openMap}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openMap();
        }
      }}
      role="button"
      tabIndex={0}
      title="Click to show map"
      style={{
        position: "relative",
        width: "100%",
        height: `${THUMB_HEIGHT}px`,
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      <img
        src={MapPlaceholder}
        alt=""
        aria-hidden="true"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          opacity: 0.8, // slightly muted so it reads as a preview
          filter: "blur(1.5px)", // light blur — recognisable but clearly a preview
          transform: "scale(1.04)", // hide the blur's soft edges
        }}
      />
      {/* faint wash for pin/label contrast over the blurred map */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15,23,32,0.08)",
        }}
      />
      <PinOverlay icon={orderTypeIcon} />
      {/* show-map affordance */}
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 7px",
          borderRadius: 6,
          background: "rgba(255,255,255,0.92)",
          color: "#33404d",
          fontSize: 11,
          fontWeight: 600,
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
          pointerEvents: "none",
        }}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
          <line x1="8" y1="2" x2="8" y2="18" />
          <line x1="16" y1="6" x2="16" y2="22" />
        </svg>
        Show map
      </div>
    </div>
  );
};

export default AddressLocationMap;
